import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import RobotInformation from '../../models/robots/robot-information';
import ValueCard from '../data-widgets/ValueCard';
import { convertSecondsToRoundedString, dateToStringFormat } from '@common/utils/date';
import useDistanceUnitValue from '@common/hooks/useDistanceUnitValue';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import { replaceArgsInMessage } from '../../utils/strings';
import { numberFormatter } from 'utils/numbers';

const RobotTotals: React.FC<{ robotInformation: RobotInformation | null }> = ({
  robotInformation,
}) => {
  const { t } = useTranslation();

  const splitFigures =
    robotInformation?.currentDeployment != null &&
    (robotInformation.totalOperatingTime !== robotInformation.currentDeployment.operatingTime ||
      robotInformation.totalCleanedArea !== robotInformation.currentDeployment.cleanedArea);

  const periodOperatingTimeSeconds = robotInformation?.currentDeployment?.operatingTime ?? 0;
  const totalOperatingTimeSeconds = robotInformation?.totalOperatingTime ?? 0;

  const headlineOperatingTimeSeconds = splitFigures
    ? periodOperatingTimeSeconds
    : totalOperatingTimeSeconds;

  const { converted: headlineCleanedArea, label: cleanedAreaLabel } = useDistanceUnitValue(
    (splitFigures
      ? robotInformation?.currentDeployment?.cleanedArea
      : robotInformation?.totalCleanedArea) ?? 0,
    DistanceValueUnit.METERS_SQUARED,
  );

  const { converted: totalCleanedArea } = useDistanceUnitValue(
    robotInformation?.totalCleanedArea ?? 0,
    DistanceValueUnit.METERS_SQUARED,
  );

  let deployment = robotInformation?.deploymentDates.start
    ? dateToStringFormat(robotInformation.deploymentDates.start, true)
    : t('unknown-value');
  deployment = robotInformation?.deploymentDates.end
    ? `${deployment} - ${dateToStringFormat(robotInformation.deploymentDates.end, true)}`
    : deployment;
  const deploymentLabel = robotInformation?.deploymentDates.end
    ? t('table-header-robot-deployment')
    : t('table-header-robot-deployed-since');

  const rootOrganizationName = robotInformation?.deploymentDates.rootOrganizationName;
  const organizationName = robotInformation?.deploymentDates.organizationName;
  const deploymentSubtitle =
    rootOrganizationName === organizationName && organizationName != null
      ? replaceArgsInMessage(t('label-deployment-at-organization'), [organizationName])
      : organizationName != null && rootOrganizationName != null
        ? replaceArgsInMessage(t('label-deployment-at-organization'), [
            `${organizationName} (${rootOrganizationName})`,
          ])
        : undefined;

  const totalOperatingTimeCaption = replaceArgsInMessage(t('label-total-of-value'), [
    convertSecondsToRoundedString(totalOperatingTimeSeconds),
  ]);

  const totalCleanedAreaCaption = replaceArgsInMessage(t('label-total-of-value'), [
    `${numberFormatter(totalCleanedArea, 2)} ${cleanedAreaLabel}`,
  ]);

  return (
    <React.Fragment>
      <Stack sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
        <Box flex={1}>
          <ValueCard
            label={deploymentLabel}
            unit={deploymentSubtitle}
            value={deployment}
            maxFontSize={40}
          />
        </Box>
        <Box flex={1}>
          <ValueCard
            label={
              splitFigures
                ? t('label-operating-time-this-deployment')
                : t('label-total-operating-time')
            }
            value={convertSecondsToRoundedString(headlineOperatingTimeSeconds)}
            unit={splitFigures ? totalOperatingTimeCaption : undefined}
            valueTestId="robot-totals-operating-time"
            maxFontSize={40}
          />
        </Box>
        <Box flex={1}>
          <ValueCard
            label={
              splitFigures ? t('label-cleaned-area-this-deployment') : t('label-total-cleaned-area')
            }
            value={`${numberFormatter(headlineCleanedArea, 2)} ${cleanedAreaLabel}`}
            unit={splitFigures ? totalCleanedAreaCaption : undefined}
            maxFontSize={40}
          />
        </Box>
      </Stack>
    </React.Fragment>
  );
};

export default RobotTotals;
